import { useState, useEffect, useRef } from "react";
import { Phone, User, MapPin, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";
import { sendLeadToGrowthHub } from "@/lib/sendLeadToGrowthHub";
import { detectBrandProfile } from "@/lib/brandRegistry";

declare global {
  interface Window {
    google: any;
  }
}

interface PlacePrediction {
  placeId: string;
  text: string;
  toPlace: () => { fetchFields: (opts: { fields: string[] }) => Promise<void> } & Record<string, any>;
}

const CallbackMicroForm = () => {
  const { toast } = useToast();
  const { trackFormSubmission } = useVisitorTracking();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const addressRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const sessionTokenRef = useRef<any>(null);
  const debounceRef = useRef<number | null>(null);


  // Pull browser/iOS-Contact autofill values into React state.
  // Browser autofill writes to the DOM directly and often skips React's
  // synthetic onChange, leaving controlled inputs empty. Syncing on focus
  // + a short poll after focus catches name + phone + address together
  // (iOS Contact card autofill fills all three at once).
  const syncFromDom = () => {
    const n = nameRef.current?.value ?? "";
    const p = phoneRef.current?.value ?? "";
    const a = addressRef.current?.value ?? "";
    if (n && n !== name) setName(n);
    if (p && p !== phone) setPhone(p);
    if (a && a !== address) setAddress(a);
  };

  // Returning visitors: prefill from details they previously submitted (stored only in their own browser).
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("obc_saved_contact") || "null");
      if (saved) {
        if (saved.name) setName(String(saved.name).slice(0, 80));
        if (saved.phone) setPhone(String(saved.phone).slice(0, 15));
        if (saved.address) setAddress(String(saved.address).slice(0, 200));
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    // Catch Chrome's webkit autofill animation hook
    const handleAnim = (e: AnimationEvent) => {
      if (e.animationName === "onAutoFillStart") syncFromDom();
    };
    document.addEventListener("animationstart", handleAnim, true);
    // Initial pass shortly after mount (browsers can autofill on load)
    const t1 = window.setTimeout(syncFromDom, 250);
    const t2 = window.setTimeout(syncFromDom, 1500);
    return () => {
      document.removeEventListener("animationstart", handleAnim, true);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAutofillFocus = () => {
    // Run a few times after focus — iOS Contact autofill fills async
    syncFromDom();
    window.setTimeout(syncFromDom, 100);
    window.setTimeout(syncFromDom, 400);
    window.setTimeout(syncFromDom, 1000);
  };

  // Load Google Maps JS with the new Places library.
  useEffect(() => {
    const apiKey =
      import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_BROWSER_KEY ||
      import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;
    if (window.google?.maps?.importLibrary) return;
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-places="1"]');
    if (existing) return;
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&v=weekly`;
    script.async = true;
    script.defer = true;
    script.dataset.googlePlaces = "1";
    document.head.appendChild(script);
  }, []);

  const fetchSuggestions = async (input: string) => {
    if (!input || input.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      if (!window.google?.maps?.importLibrary) return;
      const placesLib: any = await window.google.maps.importLibrary("places");
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
      }
      const { suggestions: results } = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: sessionTokenRef.current,
        includedRegionCodes: ["us"],
        includedPrimaryTypes: ["street_address", "premise", "subpremise", "route"],
      });
      const mapped: PlacePrediction[] = (results || [])
        .filter((s: any) => s.placePrediction)
        .map((s: any) => ({
          placeId: s.placePrediction.placeId,
          text: s.placePrediction.text?.toString?.() ?? "",
          toPlace: () => s.placePrediction.toPlace(),
        }));
      setSuggestions(mapped);
      setShowSuggestions(true);
    } catch (err) {
      console.error("Places autocomplete failed:", err);
    }
  };

  const handleAddressChange = (value: string) => {
    setAddress(value);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => fetchSuggestions(value), 200);
  };

  const handleSelectSuggestion = async (s: PlacePrediction) => {
    try {
      const place: any = s.toPlace();
      await place.fetchFields({ fields: ["formattedAddress"] });
      const formatted = place.formattedAddress || s.text;
      setAddress(formatted);
      setSuggestions([]);
      setShowSuggestions(false);
      sessionTokenRef.current = null; // session ends on selection
    } catch (err) {
      console.error("Place details fetch failed:", err);
      setAddress(s.text);
      setShowSuggestions(false);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    const trimmedAddress = address.trim();
    if (trimmedName.length < 2) {
      toast({ title: "Name required", description: "Please enter your name.", variant: "destructive" });
      return;
    }
    if (trimmedPhone.length < 7) {
      toast({ title: "Valid phone required", description: "Please enter a valid phone number.", variant: "destructive" });
      return;
    }
    if (trimmedAddress.length < 5) {
      toast({ title: "Address required", description: "Please enter the property address.", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);

    try {
      const brandProfile = detectBrandProfile();
      const { error } = await supabase
        .from("crm_leads")
        .insert({
          name: trimmedName,
          email: "callback@placeholder.com",
          phone: trimmedPhone,
          address: trimmedAddress,
          source_page: "hero-callback",
          source_brand: brandProfile.brand,
          source_hostname: typeof window !== "undefined" ? window.location.hostname : null,
        } as never);

      if (error) throw error;

      try {
        localStorage.setItem("obc_saved_contact", JSON.stringify({ name: trimmedName, phone: trimmedPhone, address: trimmedAddress }));
      } catch { /* ignore */ }

      await trackFormSubmission('callback_form', {
        name: trimmedName,
        phone: trimmedPhone,
        source_page: 'hero-callback',
        ...({ address: trimmedAddress } as Record<string, string>),
      });

      await sendLeadToGrowthHub({
        name: trimmedName,
        phone: trimmedPhone,
        address: trimmedAddress,
        form_type: 'callback_request',
        service_interest: 'Callback Request',
        notes: `Customer requested a callback from hero form. Property: ${trimmedAddress}`,
        priority: 'high',
      });

      supabase.functions.invoke('send-lead-notification', {
        body: {
          name: trimmedName,
          email: 'callback@placeholder.com',
          phone: trimmedPhone,
          address: trimmedAddress,
          service_type: 'Callback Request',
          source_page: 'hero-callback',
          message: `Customer requested a callback. Property: ${trimmedAddress}. Please call back within 15 minutes.`,
        },
      }).catch((err) => console.error('Email notification failed:', err));

      setSubmitted(true);
      toast({
        title: "We'll call you back!",
        description: "Expect a call within 15 minutes during business hours.",
      });
    } catch {
      toast({
        title: "Error",
        description: "Please try again or call us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center gap-2 bg-accent/20 border border-accent/30 rounded-full px-5 py-3">
        <Phone className="w-4 h-4 text-accent" />
        <span className="text-sm font-semibold text-primary-foreground">We'll call you shortly!</span>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-2 max-w-2xl"
      name="callback-request"
      method="post"
      action="#"
    >
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <div className="relative flex-1">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            ref={nameRef}
            id="callback-name"
            name="name"
            type="text"
            placeholder="Your name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onFocus={handleAutofillFocus}
            onAnimationStart={syncFromDom}
            className="pl-9 bg-white/95 text-foreground border-white/50 rounded-full h-11 obc-autofill-watch"
            required
            minLength={2}
            maxLength={80}
            autoComplete="name"
            autoCapitalize="words"
          />
        </div>
        <div className="relative flex-1">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            ref={phoneRef}
            id="callback-tel"
            name="tel"
            type="tel"
            inputMode="tel"
            placeholder="Your phone number *"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onFocus={handleAutofillFocus}
            onAnimationStart={syncFromDom}
            className="pl-9 bg-white/95 text-foreground border-white/50 rounded-full h-11 obc-autofill-watch"
            required
            minLength={7}
            maxLength={15}
            autoComplete="tel"
          />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <div className="relative flex-1">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
          <Input
            ref={addressRef}
            id="callback-address"
            name="street-address"
            type="text"
            placeholder="Start typing your property address…"
            value={address}
            onChange={(e) => handleAddressChange(e.target.value)}
            onFocus={() => { handleAutofillFocus(); if (suggestions.length) setShowSuggestions(true); }}
            onBlur={() => window.setTimeout(() => setShowSuggestions(false), 150)}
            onAnimationStart={syncFromDom}
            className="pl-9 bg-white/95 text-foreground border-white/50 rounded-full h-11 obc-autofill-watch"
            required
            minLength={5}
            maxLength={200}
            autoComplete="street-address"
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul
              role="listbox"
              className="absolute z-50 left-0 right-0 mt-1 bg-white border border-border rounded-2xl shadow-lg max-h-72 overflow-auto"
            >
              {suggestions.map((s) => (
                <li
                  key={s.placeId}
                  role="option"
                  aria-selected="false"
                  className="px-4 py-2 text-sm text-foreground hover:bg-muted cursor-pointer flex items-start gap-2"
                  onMouseDown={(e) => { e.preventDefault(); handleSelectSuggestion(s); }}
                >
                  <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                  <span>{s.text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Button
          type="submit"
          variant="accent"
          size="sm"
          className="rounded-full h-11 px-5 font-semibold whitespace-nowrap"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Call Me Back
              <ArrowRight className="ml-1 w-4 h-4" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default CallbackMicroForm;
